/**
 * Green Flag — EventPreSpark ("Spark before you go")
 *
 * The pre-event swipe deck for one event the viewer is registered for: only
 * that event's attendees, in the same MatchProfile card the /spark tab uses.
 * Rendered as a modal overlay at `/event/:sparkId/spark` (see Page.tsx), so
 * the page the viewer came from stays mounted behind it.
 *
 * Never says how many people are going: no counter, no progress bar.
 */

import { Box, Text } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ModalPage } from "../../../components/ModalPage";
import { LoaderScreen } from "../../../components/LoaderScreen/LoaderScreen";
import { ScreenTransitioner } from "../../../components/ScreenTransitioner/ScreenTransitioner";
import { ConfettiFlagEmptyState } from "../../../components/ConfettiFlagEmptyState/ConfettiFlagEmptyState";
import { CompleteProfileGate } from "../../../components/CompleteProfileGate/CompleteProfileGate";
import { formatEventWhen } from "../../../lib/formatEventWhen";
import { useStoreData } from "../../../lib/store/store";
import { useProfileDeck } from "../../../lib/useProfileDeck";
import { MatchProfile } from "../Match/MatchProfile";
import { FriendsModeState } from "../Spark/FriendsModeState";

export function EventPreSpark() {
  const navigate = useNavigate();
  const { sparkId: sparkIdParam } = useParams<{ sparkId: string }>();
  const sparkId = parseInt(sparkIdParam || "") || undefined;
  const storeData = useStoreData();
  const spark = useMemo(
    () => storeData.upcomingSparks?.find((s) => s.id === sparkId),
    [storeData.upcomingSparks, sparkId]
  );
  const registered = !!spark?.registered;
  const friendsOnly = storeData.user?.friends_only ?? false;
  const eventPath = `/event/atlanta/${sparkIdParam}`;

  const {
    setRelationship,
    screenTransitionerRef,
    deckScreens,
    gateRequiredSteps,
    matchCelebration,
  } = useProfileDeck({
    sparkId,
    enabled: !!sparkId && registered && !friendsOnly,
    // The full profile before the first card: the people the viewer Sparks
    // here are people they are about to meet.
    fullProfileGateThreshold: 0,
  });

  const backToEvent = useCallback(() => navigate(eventPath), [navigate, eventPath]);

  // No seat here, by the store or, when the store is behind (a cancel on
  // another device, an event that has ended), by the server.
  const notRegistered = (
    <ConfettiFlagEmptyState
      headline="This is for people going to the event"
      body="Register for the event to Spark the people you might meet there."
      cta={{ label: "View Event", onClick: backToEvent }}
      maxW="25rem"
      mx="auto"
      py="8"
    />
  );

  let body;
  if (storeData.loading) {
    body = <LoaderScreen />;
  } else if (!spark || !registered) {
    body = notRegistered;
  } else if (friendsOnly) {
    body = <FriendsModeState maxW="25rem" mx="auto" py="8" />;
  } else {
    body = (
      <ScreenTransitioner
        ref={screenTransitionerRef}
        minHeight="100%"
        screens={{
          ...deckScreens((profile, behind) => (
            <MatchProfile
              profile={profile}
              behindProfile={behind}
              onDecision={setRelationship}
            />
          )),
          notRegistered,
          noMoreProfiles: (
            <ConfettiFlagEmptyState
              headline="You're all caught up"
              body="You've seen everyone who's signed up so far. New people may join before the event — check back."
              cta={{ label: "Back to Event", onClick: backToEvent }}
              maxW="25rem"
              mx="auto"
              w="100%"
              justifyContent="center"
            />
          ),
        }}
        startIndex="loader"
      />
    );
  }

  return (
    <>
      <ModalPage
        motionKey="event-pre-spark-modal"
        title="Spark before you go"
        fallback={eventPath}
        bodyBg="bg.canvas"
        stickyBelowHeader={
          spark ? (
            <Box
              px="4"
              pb="3"
              textAlign="center"
              borderBottomWidth="1px"
              borderColor="border.subtle"
            >
              <Text textStyle="eyebrow" color="fg.muted" truncate>
                {spark.title} · {formatEventWhen(spark.starts_at, { tonight: true })}
              </Text>
            </Box>
          ) : undefined
        }
      >
        <Box maxW="40rem" mx="auto" w="full" h="full">
          {body}
        </Box>
      </ModalPage>
      {registered && !friendsOnly && (
        <CompleteProfileGate requiredSteps={[...gateRequiredSteps]} />
      )}
      {matchCelebration}
    </>
  );
}
